import { Link } from "react-router-dom";
import styled, { css, CSSObject } from "styled-components";
import {
  BLACK,
  FROSTED_BLACK,
  LIGHT_BLUE,
  NEAR_BLACK,
  NEAR_WHITE,
  RED,
} from "../config";
import { Status } from "../types";
import { getColorByStatusCode, statusCodeMapping } from "../utils";

export function getTextWidth(
  text: string,
  stripeposition: "left" | "top" = "left",
  font = "bold 12px Roboto",
): number {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return 0;
  context.font = font;
  const metrics = context.measureText(text);
  const padding = stripeposition === "left" ? 18 : 0;
  return Math.ceil(metrics.width + padding);
}

export function getModalPadding(
  stripecontent?: string,
  stripeposition?: "left" | "top",
): string {
  if (stripeposition === "top") return "24px 10px 6px 10px";

  return `10px 10px 10px ${stripecontent ? getTextWidth(stripecontent) : 18}px`;
}

export const LeftStripe = css`
  content: ${({
    stripecontent,
  }: {
    stripecontent?: string;
    statuscode?: Status;
  }) => (stripecontent ? `'${stripecontent}'` : "''")};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ stripecontent }) => (stripecontent ? "fit-content" : "8px")};
  padding: 0 ${({ stripecontent }) => (stripecontent ? "6px" : "0")};
  background-color: ${({ statuscode }) =>
    statuscode !== undefined ? statusCodeMapping[statuscode] : undefined};
  color: ${BLACK};
  font-size: 12px;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
`;

export const TopStripe = css`
  content: ${({
    stripecontent,
  }: {
    stripecontent?: string;
    statuscode?: Status;
  }) => (stripecontent ? `'\\00a0\\00a0${stripecontent}'` : "''")};
  display: flex;
  align-items: center;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  height: ${({ stripecontent }) => (stripecontent ? "fit-content" : "8px")};
  padding: ${({ stripecontent }) => (stripecontent ? "2px" : "0")} 0;
  background-color: ${({ statuscode }) =>
    statuscode !== undefined ? statusCodeMapping[statuscode] : undefined};
  color: ${BLACK};
  font-size: 12px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  white-space: nowrap;
`;

export const LinkedItemBox = styled(Link)<{
  stripecontent?: string;
  statuscode?: Status;
}>`
  background-color: ${FROSTED_BLACK};
  color: ${NEAR_WHITE};
  font-weight: bold;
  border-radius: 5px;
  text-decoration: none;
  text-align: center;
  font-size: 12px;
  line-height: 16px;
  padding: 10px;
  cursor: ${({ to }) => (to ? "pointer" : "default")};

  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
  }
`;

export const ModalItemWithStripe = styled(LinkedItemBox)<{
  stripeposition?: "left" | "top";
}>`
  background-color: ${BLACK};
  border: ${({ statuscode }) =>
    statuscode !== undefined
      ? `1px solid ${statusCodeMapping[statuscode]}`
      : undefined};
  padding: ${({ stripecontent, stripeposition }) =>
    getModalPadding(stripecontent, stripeposition)};
  position: relative;
  width: ${({ stripeposition, stripecontent }) =>
    stripeposition === "top"
      ? `${getTextWidth(stripecontent ?? "", "top")}px`
      : "fit-content"};
  text-align: ${({ stripeposition }) =>
    stripeposition === "top" ? "start" : "inherit"};
  white-space: nowrap;

  &::before {
    ${({ stripeposition }) =>
      stripeposition === "top" ? TopStripe : LeftStripe};
  }
`;

export const CircleStatus = styled.div.attrs({
  "data-testid": "circle-status",
})<{
  status?: Status;
  color?: string;
}>`
  width: 25px;
  height: 25px;
  border-radius: 100%;
  background-color: ${({ status, color }) =>
    status !== undefined ? getColorByStatusCode(status) : color};
`;

export const ContainerPage = styled.div<{ isLoading?: boolean }>`
  height: 95%;
  background-color: ${NEAR_BLACK};
  border-radius: 10px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  padding: 1rem 2rem 0.5rem 2rem;
  display: flex;
  flex-direction: column;
  cursor: ${({ isLoading }) => (isLoading ? "wait" : "default")};
`;

export const StyledLinkGlobal = styled(Link)<{ styledHover?: CSSObject }>`
  text-decoration: none;
  color: ${NEAR_WHITE};
  font-weight: 700;

  &:hover {
    ${({ styledHover }) => styledHover || `color: ${LIGHT_BLUE} !important;`};
  }
  &:focus,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
  }
`;

export const StyledHrGlobal = styled.hr<{ customColor?: string }>`
  width: 100%;
  border-color: ${({ customColor }) => customColor || "inherit"};
`;

export const ButtonSpacing = styled.div`
  margin-right: 16px;
`;

export const AffectedTextContent = styled.div`
  width: 100%;
  height: 85%;
  display: flex;
  align-items: end;
  justify-content: end;
`;

export const AffectedText = styled.span`
  font-style: normal;
  font-weight: 300;
  font-size: 36px;
  line-height: 42px;
  text-align: center;
  margin-bottom: -3px;
  color: ${RED};
`;

export const TotalMahText = styled.span`
  font-style: normal;
  font-weight: 300;
  font-size: 24px;
  line-height: 28px;
  text-align: center;

  color: ${NEAR_WHITE};
`;

//REFACTOR: Passar os styles de tagstatus para outro file
export const StatusTag = styled.div.attrs({ "data-testid": "status-tag" })<{
  statusCode: Status;
}>`
  width: 24px;
  height: 24px;
  border-radius: 100%;
  background-color: ${({ statusCode }) => getColorByStatusCode(statusCode)};
`;
